import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Check, Download } from "lucide-react";

const APP_URL = "https://app.shescan.app";

/**
 * Fire a GA4 event. GA4-only by design — /kit is for verified brand owners,
 * a trust-based surface. No Meta Pixel, no Pinterest, no ad-network beacons.
 */
function track(event, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, { surface: "kit", ...params });
    }
  } catch {
    /* analytics failures must never break UX */
  }
}

const CAPTION = `Something we didn't ask for and are quietly thrilled about: we've been verified on SheScan — a new app that lets you scan a barcode and find out if a brand is women-owned.

Every brand on it is checked by a human. Not scraped. Not self-reported. Someone actually looked.

If you've ever stood in an aisle wondering who you're actually paying — that's the whole app.

https://app.shescan.app`;

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  // iOS Safari / non-secure fallback
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-1000px";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  return ok;
}

function useCopyButton(text, eventName) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      const ok = await copyText(text);
      if (!ok) throw new Error();
      setCopied(true);
      if (eventName) track(eventName);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };
  return { copied, onCopy };
}

/**
 * Fetch a file as a blob then trigger a real download.
 * Works even when the browser would otherwise open the image inline
 * (some mobile browsers ignore the <a download> attribute).
 */
async function downloadFile(url, filename) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch {
    // Fallback: open the raw file in a new tab so the user can save it manually.
    window.open(url, "_blank", "noopener,noreferrer");
    toast.info("Long-press the image to save it.");
  }
}

export default function Kit() {
  const captionBtn = useCopyButton(CAPTION, "kit_copy_caption");
  const linkBtn = useCopyButton(APP_URL, "kit_copy_link");

  useEffect(() => {
    track("kit_view");
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">
      <main className="mx-auto max-w-[720px] px-5 sm:px-8 py-16 sm:py-24 space-y-16 sm:space-y-20">
        {/* --- Header --- */}
        <header data-testid="kit-header" className="space-y-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-orange">
            For verified brands
          </span>
          <h1
            data-testid="kit-title"
            className="font-display font-bold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            You&apos;re on SheScan&trade;.
          </h1>
          <p className="text-lg sm:text-xl text-ink-soft leading-relaxed">
            We verified you by hand. You didn&apos;t pay for this, and you
            can&apos;t — verification isn&apos;t for sale. Here&apos;s
            everything you need to tell people, in about two minutes.
          </p>
        </header>

        {/* --- 1. Badge --- */}
        <section data-testid="kit-badge" className="space-y-6">
          <SectionHeading number="01" title="The badge" />
          <div className="border border-line bg-white flex items-center justify-center py-10 sm:py-14">
            <img
              src="/kit/shescan-badge.png"
              alt="SheScan verified women-owned badge"
              className="w-40 sm:w-56 h-auto"
              data-testid="kit-badge-image"
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <DownloadBtn
              testid="kit-badge-download"
              onClick={() => {
                track("kit_download_badge");
                downloadFile("/kit/shescan-badge.png", "shescan-badge.png");
              }}
              label="Download badge (PNG)"
            />
          </div>
        </section>

        {/* --- 2. Caption --- */}
        <section data-testid="kit-caption" className="space-y-6">
          <SectionHeading number="02" title="The caption" />
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft">
            Paste it as-is, or make it yours.
          </p>
          <pre
            data-testid="kit-caption-text"
            className="whitespace-pre-wrap font-sans text-[15px] sm:text-base leading-relaxed text-ink border border-line bg-white p-5 sm:p-6"
          >
            {CAPTION}
          </pre>
          <button
            type="button"
            data-testid="kit-caption-copy"
            onClick={captionBtn.onCopy}
            className={`inline-flex items-center justify-center gap-2 font-sans font-bold uppercase text-xs tracking-[0.22em] px-6 py-4 border-2 transition-colors ${
              captionBtn.copied
                ? "border-ink bg-ink text-white"
                : "border-brand-orange bg-brand-orange text-white hover:bg-white hover:text-ink hover:border-line"
            }`}
          >
            {captionBtn.copied ? (
              <>
                <Check className="w-4 h-4" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy caption
              </>
            )}
          </button>
        </section>

        {/* --- 3. Images --- */}
        <section data-testid="kit-images" className="space-y-6">
          <SectionHeading number="03" title="The images" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ImageCard
              testid="kit-image-square"
              src="/kit/shescan-square.png"
              alt="SheScan square image for Instagram feed"
              aspect="aspect-square"
              label="Square (feed)"
              onDownload={() => {
                track("kit_download_square");
                downloadFile("/kit/shescan-square.png", "shescan-square.png");
              }}
            />
            <ImageCard
              testid="kit-image-story"
              src="/kit/shescan-story.png"
              alt="SheScan vertical image for Instagram story"
              aspect="aspect-[9/16]"
              label="Vertical (story)"
              onDownload={() => {
                track("kit_download_story");
                downloadFile("/kit/shescan-story.png", "shescan-story.png");
              }}
            />
          </div>
        </section>

        {/* --- 4. Link --- */}
        <section data-testid="kit-link" className="space-y-6">
          <SectionHeading number="04" title="The link" />
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              readOnly
              value={APP_URL}
              data-testid="kit-link-input"
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-white border border-line text-ink px-4 py-4 font-mono text-sm focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors"
            />
            <button
              type="button"
              data-testid="kit-link-copy"
              onClick={linkBtn.onCopy}
              className={`inline-flex items-center justify-center gap-2 font-sans font-bold uppercase text-xs tracking-[0.22em] px-6 py-4 border-2 transition-colors shrink-0 ${
                linkBtn.copied
                  ? "border-ink bg-ink text-white"
                  : "border-brand-orange bg-brand-orange text-white hover:bg-white hover:text-ink hover:border-line"
              }`}
            >
              {linkBtn.copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy link
                </>
              )}
            </button>
          </div>
        </section>

        {/* --- Footer --- */}
        <footer
          data-testid="kit-footer"
          className="pt-10 border-t border-line font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft"
        >
          Questions?{" "}
          <a
            href="mailto:rene@thewomenseconomy.org"
            className="text-ink hover:text-brand-orange transition-colors underline underline-offset-4"
          >
            rene@thewomenseconomy.org
          </a>
        </footer>
      </main>
    </div>
  );
}

function SectionHeading({ number, title }) {
  return (
    <div className="flex items-baseline gap-4 border-b border-line pb-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
        {number}
      </span>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink leading-[1.15]">
        {title}
      </h2>
    </div>
  );
}

function DownloadBtn({ testid, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center justify-center gap-2 font-sans font-bold uppercase text-xs tracking-[0.22em] px-5 py-4 border-2 border-brand-orange bg-brand-orange text-white hover:bg-white hover:text-ink hover:border-line transition-colors"
    >
      <Download className="w-4 h-4" />
      {label}
    </button>
  );
}

function ImageCard({ testid, src, alt, aspect, label, onDownload }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      <div
        className={`border border-line bg-white overflow-hidden flex items-center justify-center ${aspect}`}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <button
        type="button"
        onClick={onDownload}
        data-testid={`${testid}-download`}
        className="w-full inline-flex items-center justify-center gap-2 font-sans font-bold uppercase text-xs tracking-[0.22em] px-5 py-3 border-2 border-line bg-white text-ink hover:border-brand-orange transition-colors"
      >
        <Download className="w-4 h-4" />
        {label}
      </button>
    </div>
  );
}
